import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import './FallingPetalsCSS.css';
import { createShapeCanvas } from './shapes';
import { recolorGradient } from './recolor';
import lotusGradient from '@shared/assets/falling-petals/lotus-gradient.png';

function randomBetween(min, max) {
    return Math.random() * (max - min) + min;
}

/**
 * 飄落整朵蓮花 / 菩提葉特效 - 純 CSS 實作
 * Falling whole lotus flowers / bodhi leaves - Pure CSS implementation
 *
 * 效能最佳，適合作為全站背景裝飾層。蓮花使用成熟美術素材（B 素材，側視漸層彩色，
 * 取自 freesvg.org CC0），菩提葉維持程式繪製。兩者飄落時都會持續翻轉，像真實花瓣/落葉打轉飄落。
 */
const FallingPetalsCSS = ({
    type = 'lotus',
    count = 20,
    color = '#F5C97B',
    minSize = 32,
    maxSize = 64,
    minDuration = 8,
    maxDuration = 16,
    className = '',
}) => {
    const [imageUrl, setImageUrl] = useState(null);

    useEffect(() => {
        let cancelled = false;
        if (type === 'bodhi') {
            const canvas = createShapeCanvas(type, 160, color);
            setImageUrl(canvas.toDataURL('image/png'));
        } else {
            recolorGradient(lotusGradient, color).then((canvas) => {
                if (!cancelled) setImageUrl(canvas.toDataURL('image/png'));
            });
        }
        return () => { cancelled = true; };
    }, [type, color]);

    const petals = useMemo(() => {
        return Array.from({ length: count }, (_, i) => ({
            id: i,
            left: randomBetween(0, 100),
            size: randomBetween(minSize, maxSize),
            duration: randomBetween(minDuration, maxDuration),
            delay: randomBetween(0, maxDuration),
            swayDuration: randomBetween(2.5, 5),
            rotateDuration: randomBetween(4, 9),
            spinDirection: Math.random() > 0.5 ? 1 : -1,
            opacity: randomBetween(0.6, 0.95),
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [count, minSize, maxSize, minDuration, maxDuration]);

    if (!imageUrl) return null;

    return (
        <div className={`falling-petals-css ${className}`} aria-hidden="true">
            {petals.map((p) => (
                <div
                    key={p.id}
                    className="falling-petal-fall"
                    style={{
                        left: `${p.left}%`,
                        animationDuration: `${p.duration}s`,
                        animationDelay: `${-p.delay}s`,
                    }}
                >
                    <div
                        className="falling-petal-sway"
                        style={{
                            animationDuration: `${p.swayDuration}s`,
                            animationDelay: `${-p.delay}s`,
                        }}
                    >
                        <div
                            className="falling-petal-spin"
                            style={{
                                animationDuration: `${p.rotateDuration}s`,
                                animationDirection: p.spinDirection === 1 ? 'normal' : 'reverse',
                                animationDelay: `${-p.delay}s`,
                            }}
                        >
                            <img
                                src={imageUrl}
                                alt=""
                                width={p.size}
                                height={p.size}
                                style={{ opacity: p.opacity, display: 'block' }}
                            />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

FallingPetalsCSS.propTypes = {
    type: PropTypes.oneOf(['lotus', 'bodhi']),
    count: PropTypes.number,
    color: PropTypes.string,
    minSize: PropTypes.number,
    maxSize: PropTypes.number,
    minDuration: PropTypes.number,
    maxDuration: PropTypes.number,
    className: PropTypes.string,
};

export default FallingPetalsCSS;
